import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import CloseIcon from '@mui/icons-material/Close';
import firebase from '../../config/firebase';
import notificationPreferencesApi from '../../service/modules/notificationPreferences.api';

const LEVEL_CHIP = {
  Informativo: { color: 'info' },
  Atenção: { color: 'warning' },
  Crítico: { color: 'error' },
  Sucesso: { color: 'success' },
};

function authUidConflictsWith(userUid) {
  const authUid = firebase.auth().currentUser?.uid || '';
  return Boolean(userUid && authUid && authUid !== userUid);
}

function NotificationPreferencesModal({ open, onClose, userUid }) {
  const [types, setTypes] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingTypeId, setSavingTypeId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const seqByType = useRef({});

  const load = useCallback(async () => {
    if (authUidConflictsWith(userUid)) {
      setPreferences({});
      setTypes([]);
      setError('A sessão não é mais deste usuário. Entre de novo para ver as preferências.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [typesRes, prefsRes] = await Promise.all([
        notificationPreferencesApi.listTypes(),
        notificationPreferencesApi.getPreferences(),
      ]);
      if (authUidConflictsWith(userUid)) {
        setPreferences({});
        setError('A sessão mudou durante a consulta. Entre de novo.');
        return;
      }
      const typeList = typesRes?.data?.data ?? typesRes?.data ?? [];
      const prefs =
        prefsRes?.data?.data?.preferences ??
        prefsRes?.data?.preferences ??
        {};
      setTypes(Array.isArray(typeList) ? typeList : []);
      setPreferences(prefs && typeof prefs === 'object' ? prefs : {});
    } catch (err) {
      console.error('Erro ao carregar preferências:', err);
      setError('Não foi possível carregar as preferências.');
      setTypes([]);
      setPreferences({});
    } finally {
      setLoading(false);
    }
  }, [userUid]);

  useEffect(() => {
    setPreferences({});
    setTypes([]);
    seqByType.current = {};
  }, [userUid]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const handleToggle = async (typeId, nextEnabled) => {
    if (authUidConflictsWith(userUid)) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'A sessão não é mais deste usuário. Preferência não foi salva.',
      });
      return;
    }

    const prev = preferences[typeId] !== false;
    const seq = (seqByType.current[typeId] || 0) + 1;
    seqByType.current[typeId] = seq;

    setPreferences((p) => ({ ...p, [typeId]: nextEnabled }));
    setSavingTypeId(typeId);

    try {
      await notificationPreferencesApi.updatePreference(typeId, nextEnabled);
      if (seqByType.current[typeId] !== seq) return;
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Preferência salva.',
      });
    } catch (err) {
      console.error('Erro ao salvar preferência:', err);
      if (seqByType.current[typeId] !== seq) return;
      setPreferences((p) => ({ ...p, [typeId]: prev }));
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Falha ao salvar. Preferência restaurada.',
      });
    } finally {
      if (seqByType.current[typeId] === seq) {
        setSavingTypeId(null);
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Preferências de Notificações
          <IconButton
            aria-label="Fechar"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Escolha quais notificações você deseja receber na plataforma.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ overflow: 'auto' }}>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          )}
          {!loading && error && (
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={load}>
                Tentar de novo
              </Button>
            }>
              {error}
            </Alert>
          )}
          {!loading && !error && types.length === 0 && (
            <Typography color="text.secondary">
              Nenhum tipo de notificação disponível.
            </Typography>
          )}
          {!loading && !error && types.length > 0 && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Nome do evento</TableCell>
                  <TableCell>Módulo</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Nível</TableCell>
                  <TableCell align="center">Ativo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {types.map((type) => {
                  const enabled = preferences[type.typeId] !== false;
                  const chip = LEVEL_CHIP[type.level] || { color: 'default' };
                  return (
                    <TableRow key={type.typeId} hover>
                      <TableCell>{type.name}</TableCell>
                      <TableCell>{type.module}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {type.description}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={type.level}
                          color={chip.color}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={enabled}
                          disabled={savingTypeId === type.typeId}
                          onChange={(e) =>
                            handleToggle(type.typeId, e.target.checked)
                          }
                          inputProps={{
                            'aria-label': `Ativar ${type.name}`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default NotificationPreferencesModal;
